"use client"

import { useEffect, useRef, useState } from "react"
import { Check, ChevronDown, Globe } from "lucide-react"

const languages = [
  { code: "zh-TW", name: "繁體中文", flag: "🇹🇼" },
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "zh-CN", name: "简体中文", flag: "🇨🇳" },
  { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
]

const GoogleTranslate = () => {
  const [currentLang, setCurrentLang] = useState("zh-TW")
  const [isTranslated, setIsTranslated] = useState(false)
  const [isGoogleTranslateLoaded, setIsGoogleTranslateLoaded] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [shouldLoadTranslate, setShouldLoadTranslate] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!shouldLoadTranslate) return
    if (window.google?.translate || document.querySelector("script#google-translate-script")) {
      setIsGoogleTranslateLoaded(true)
      return
    }

    const script = document.createElement("script")
    script.id = "google-translate-script"
    script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"
    script.async = true

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "zh-TW",
          autoDisplay: false,
          includedLanguages: languages.map((lang) => lang.code).join(","),
        },
        "google_translate_element"
      )
      setIsGoogleTranslateLoaded(true)
    }

    document.body.appendChild(script)

    if (!document.getElementById("google_translate_element")) {
      const div = document.createElement("div")
      div.id = "google_translate_element"
      div.style.display = "none"
      document.body.appendChild(div)
    }

    return () => {
      if (window.googleTranslateElementInit) {
        delete window.googleTranslateElementInit
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }
    }
  }, [shouldLoadTranslate])

  const changeLanguage = (langCode) => {
    if (!isGoogleTranslateLoaded && langCode !== "zh-TW") {
      setShouldLoadTranslate(true)
      return
    }

    const selectElement = document.querySelector(".goog-te-combo")

    if (langCode === "zh-TW") {
      if (selectElement) {
        selectElement.value = ""
        selectElement.dispatchEvent(new Event("change"))
      }
      setCurrentLang("zh-TW")
      setIsTranslated(false)
      setMenuOpen(false)
      return
    }

    const attemptChange = () => {
      const selectElement = document.querySelector(".goog-te-combo")
      if (selectElement) {
        selectElement.value = langCode
        selectElement.dispatchEvent(new Event("change"))
        setCurrentLang(langCode)
        setIsTranslated(true)
        setMenuOpen(false)
      } else {
        setTimeout(attemptChange, 300)
      }
    }
    attemptChange()
  }

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const banner = document.querySelector(".goog-te-banner-frame")
      const select = document.querySelector(".goog-te-combo")

      if (!banner && select?.value !== "" && currentLang !== "zh-TW") {
        select.value = ""
        select.dispatchEvent(new Event("change"))
        setCurrentLang("zh-TW")
        setIsTranslated(false)
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [currentLang])

  useEffect(() => {
    if (isGoogleTranslateLoaded) {
      changeLanguage("zh-TW")
    }
  }, [isGoogleTranslateLoaded])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const currentLanguage = languages.find((lang) => lang.code === currentLang) || languages[0]

  return (
    <div className="relative inline-block text-sm" ref={menuRef}>
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="flex items-center gap-2 border rounded px-3 py-2 text-sm hover:bg-gray-100"
      >
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline-flex items-center gap-1">
          <span className="mr-1">{currentLanguage.flag}</span>
          {currentLanguage.name}
        </span>
        <ChevronDown className="h-3 w-3 opacity-50" />
      </button>
      {menuOpen && (
        <div className="absolute z-10 mt-1 w-40 bg-white border border-gray-200 rounded shadow right-0 max-w-[90vw] overflow-x-auto">
          {languages.map((language) => (
            <div
              key={language.code}
              className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                currentLang === language.code ? "bg-gray-100 font-medium" : ""
              }`}
              onClick={() => changeLanguage(language.code)}
            >
              <span className="mr-1">{language.flag}</span>
              <span className="flex-1">{language.name}</span>
              {currentLang === language.code && <Check className="h-4 w-4" />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default GoogleTranslate
